package dev.lingvo.shared

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
enum class Role { OWNER, ADMIN, TRANSLATOR, VIEWER }

@Serializable
enum class TranslationState { EMPTY, TRANSLATED, APPROVED }

@Serializable
enum class FileFormat(val id: String, val displayName: String, val extension: String) {
    @SerialName("ANDROID_XML")
    ANDROID_XML("android_xml", "Android XML", "xml"),

    @SerialName("IOS_STRINGS")
    IOS_STRINGS("ios_strings", "iOS .strings", "strings"),

    @SerialName("JSON_NESTED")
    JSON_NESTED("json_nested", "JSON (nested)", "json"),

    @SerialName("JSON_FLAT")
    JSON_FLAT("json_flat", "JSON (flat)", "json"),

    @SerialName("PROPERTIES")
    PROPERTIES("properties", "Java properties", "properties"),

    @SerialName("YAML")
    YAML("yaml", "YAML", "yaml"),

    @SerialName("CSV")
    CSV("csv", "CSV", "csv"),

    @SerialName("XLIFF")
    XLIFF("xliff", "XLIFF 1.2", "xliff"),

    @SerialName("PO")
    PO("po", "Gettext PO", "po"),

    @SerialName("XCSTRINGS")
    XCSTRINGS("xcstrings", "Apple String Catalog", "xcstrings"),

    @SerialName("IOS_STRINGSDICT")
    IOS_STRINGSDICT("ios_stringsdict", "iOS .stringsdict", "stringsdict");

    companion object {
        fun fromId(id: String): FileFormat? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }
    }
}

@Serializable
enum class PluralCategory { ZERO, ONE, TWO, FEW, MANY, OTHER }

/**
 * How a `json_flat` file spells out a plural — the convention `CatalogueConventions` normalizes,
 * not part of the JSON syntax itself.
 *
 * [NESTED] is `json_flat`'s original behaviour: `"key": {"one": "...", "other": "..."}`. [SUFFIX] is
 * the i18next/FormatJS shape this repo's own `en.json` actually uses: `"key.one": "...",
 * "key.other": "..."` as sibling root keys. A file that never says otherwise is [NESTED], so this
 * choice is opt-in and the wire format stays backward compatible with every config and request that
 * predates it.
 */
@Serializable
enum class PluralStyle(val id: String) {
    @SerialName("nested")
    NESTED("nested"),

    @SerialName("suffix")
    SUFFIX("suffix"),
    ;

    companion object {
        fun fromId(id: String): PluralStyle? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }
    }
}

@Serializable
enum class QaSeverity { ERROR, WARNING }

@Serializable
enum class QaCheck {
    EMPTY,
    PLACEHOLDER_MISMATCH,
    MISSING_PLURAL_FORM,
    LENGTH_EXCEEDED,
    LEADING_TRAILING_WHITESPACE,
    SAME_AS_SOURCE,
    TAG_MISMATCH,
    GLOSSARY_VIOLATION,

    /**
     * A straight `"` in a locale whose typography expects something else.
     *
     * A **warning**, deliberately. A straight quote inside a code sample or a measurement (`5" pipe`)
     * is correct, and blocking approval over punctuation would teach people to ignore the QA panel —
     * which is where the errors that do matter live.
     */
    WRONG_QUOTE_STYLE,
    ;

    /**
     * What this check costs when a project has not said otherwise.
     *
     * Lives here rather than only inside the engine so the settings page can show a check nobody has
     * touched at its real severity. Two lists of eight checks is one list that eventually disagrees
     * with the engine, and the one that disagrees is the one telling a customer their release is
     * blocked by something that is not blocking it.
     *
     * The four ERRORs are the ones that crash the customer's app. Everything else is advice.
     */
    val defaultSeverity: QaSeverity
        get() = when (this) {
            EMPTY, PLACEHOLDER_MISMATCH, MISSING_PLURAL_FORM, TAG_MISMATCH -> QaSeverity.ERROR
            LENGTH_EXCEEDED, LEADING_TRAILING_WHITESPACE, SAME_AS_SOURCE,
            GLOSSARY_VIOLATION, WRONG_QUOTE_STYLE,
            -> QaSeverity.WARNING
        }
}

/** What kind of app a project localizes. Drives `lingvo init` suggestions and codegen targets. */
@Serializable
enum class ProjectKind { KMP, ANDROID, IOS, WEB, OTHER }

/** Stable project-mark choices. Stored by name so clients can render known Lucide geometry. */
@Serializable
enum class ProjectIconKey {
    MONOGRAM,
    BRACES,
    GLOBE_2,
    SMARTPHONE,
    APP_WINDOW,
    BOX,
    BOOK_OPEN,
    GAMEPAD_2,
}

/** Stable project-mark colour choices. Arbitrary colour values are deliberately not accepted. */
@Serializable
enum class ProjectAccentKey {
    INK,
    COBALT,
    JADE,
    AMBER,
    CORAL,
    VIOLET,
}

/** Where a translation value came from. Machine output is badged and excluded from TM until approved. */
@Serializable
enum class TranslationOrigin {
    HUMAN,
    MACHINE,
    IMPORT,

    /**
     * Filled from the workspace's own translation memory, at no cost.
     *
     * Its own value rather than folding into IMPORT or MACHINE, because the three answer different
     * questions a reviewer actually asks: a human wrote it, a model guessed it, a file carried it,
     * or *we already had it*. Only the last one is both free and previously approved, and only a
     * distinct value makes it filterable — "show me everything the memory filled" is the first thing
     * anyone wants after a bulk prefill.
     */
    MEMORY,
}

@Serializable
enum class MtJobState { RUNNING, DONE, FAILED }

@Serializable
enum class MtProvider(val id: String, val displayName: String, val requiresKey: Boolean) {
    @SerialName("CLAUDE")
    CLAUDE("claude", "Claude", true),

    @SerialName("OPENAI")
    OPENAI("openai", "OpenAI", true),

    @SerialName("DEEPL")
    DEEPL("deepl", "DeepL", true),

    @SerialName("GOOGLE")
    GOOGLE("google", "Google Translate", true),

    @SerialName("LIBRETRANSLATE")
    LIBRETRANSLATE("libretranslate", "LibreTranslate", false),

    @SerialName("MYMEMORY")
    MYMEMORY("mymemory", "MyMemory (free)", false);

    companion object {
        fun fromId(id: String): MtProvider? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }
    }
}
